import { Field, Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { bindActionCreators } from 'redux';
import _actions from '../actions';
import DateTimeInfo from '../components/common/DateTimeInfo';
import DismissableAlert from '../components/common/DismissableAlert';
import ExpiredPackages from '../components/common/ExpiredPackages';
import { FormBackButton } from '../components/common/FormBackButton';
import { FormHeader } from '../components/common/FormHeader';
import { FormReset } from '../components/common/FormReset';
import { FormResetButton } from '../components/common/FormResetButton';
import { FormSubheader } from '../components/common/FormSubheader';
import { FormSubmit } from '../components/common/FormSubmit';
import { FormSubmitButton } from '../components/common/FormSubmitButton';
import { FormWrapper } from '../components/common/FormWrapper';
import DealItemReview from '../components/deals/DealItemReview';
import { AddOnTotals } from '../components/package/AddOnTotal';
import { PackageAddOnsReview } from '../components/package/PackageAddOnsReview';
import emptySale from '../models/sale';
import { first } from '../utils';
import addOnKey from '../utils/addOnKey';
import dealKey from '../utils/dealKey';
import hasAddOns from '../utils/hasAddOns';
import packageKey from '../utils/packageKey';
import PromoCode from './PromoCode';
import ReservationTimer from './ReservationTimer';
import './Review.css';
import ValidatePassNumber from './ValidatePassNumber';

const ReviewContainer = ({
  error,
  sale,
  actions,
  loading,
  inventory,
  config,
  done,
  validatedPasses
}) => {
  const inventoryPkgs = inventory.packages[packageKey(sale.day)] || [];

  useEffect(() => {
    if (sale.id == null || sale.day == null || Object.keys(sale.packages) < 1) {
      return actions.transitionTo('/');
    }

    if (done) {
      return actions.transitionTo('/thanks');
    }
  }, []);

  const getInitialPassNumbers = () => {
    let numPassesToValidate = 0;
    let packageIds = Object.keys(sale.packages);

    for (const pkgId of packageIds) {
      if (!config.validateTimedPassesIndividually) {
        numPassesToValidate = 1;
        break;
      }

      const salePkg = sale.packages[pkgId];
      let numPkgs = salePkg.qty;

      // if configured, use addOn qty as basis instead of pkg qty
      if (config.validationQuantityFromAddOns) {
        if (sale.packageAddOns[pkgId]) {
          Object.keys(sale.packageAddOns[pkgId]).forEach(addOnId => {
            const addOnQty = sale.packageAddOns[pkgId][addOnId].qty || 0;
            if (addOnQty > numPkgs) {
              numPkgs = addOnQty;
            }
          });
        }
      }

      // checking if the current package's id is in the list of validationExceptions
      // if it is, do not include it in the max number packages calculation
      if (config.validationExceptions.length > 0) {
        if (!config.validationExceptions.some(id => id === parseInt(pkgId))) {
          numPassesToValidate += numPkgs;
        }
      } else {
        numPassesToValidate += numPkgs;
      }
    }

    const arrayOfPassNumbersOfMaxNumPkgs = new Array(numPassesToValidate).fill({
      pass_no: '',
      benefitIds: []
    });

    return validatedPasses.length > 0
      ? arrayOfPassNumbersOfMaxNumPkgs.map((_, idx) => {
          if (validatedPasses[idx]) {
            return {
              pass_no: validatedPasses[idx].pass_no,
              benefitIds: validatedPasses[idx].benefitIds
            };
          }
          return {
            pass_no: '',
            benefitIds: []
          };
        })
      : arrayOfPassNumbersOfMaxNumPkgs;
  };

  return (
    <FormWrapper>
      <FormHeader>
        <ReservationTimer sale={sale}>
          Buy Tickets: Review Your Order
        </ReservationTimer>
      </FormHeader>
      <FormSubheader>
        <DateTimeInfo date={sale.day} config={config} />
        <ExpiredPackages sale={sale} actions={actions} />
      </FormSubheader>
      <Formik
        enableReinitialize={true}
        initialValues={{
          packageAddOns: sale.packageAddOns,
          deals: sale.deals,
          passNumbers: getInitialPassNumbers()
        }}
        onSubmit={(_, { setSubmitting }) => {
          setSubmitting(false);
          actions.transitionTo('/checkout');
        }}
      >
        {({
          values,
          isSubmitting,
          dirty,
          validateForm,
          errors,
          handleChange,
          setFieldValue
        }) => {
          const disabled =
            isSubmitting ||
            loading ||
            inventory.loading ||
            Object.keys(errors).length > 0 ||
            dirty;

          return (
            <Form>
              <Field
                name="packageAddOns"
                render={({ field }) => {
                  return Object.keys(sale.packages).map(pkgId => {
                    const salePkg = sale.packages[pkgId];
                    const numPkgs = salePkg.qty;
                    const pkg = first(inventoryPkgs, p => p.item_id == pkgId);
                    if (pkg == null) return null;

                    const addOns = inventory.addOns[addOnKey(sale.day, pkg)];
                    return (
                      <PackageAddOnsReview
                        key={pkgId}
                        value={field.value[pkgId] || {}}
                        addOns={addOns}
                        pkg={pkg}
                        num={numPkgs}
                        sale={sale}
                        salePkg={salePkg}
                        config={config}
                      />
                    );
                  });
                }}
              />

              <Field
                name="deals"
                render={({ field }) => {
                  const deals = inventory.deals[dealKey(sale.day)];
                  if (
                    deals == null ||
                    deals.length < 1 ||
                    field.value == null ||
                    Object.keys(field.value).length < 1
                  ) {
                    return <div></div>;
                  }

                  return (
                    <div className="swformfield-additional-deals">
                      <div className="swformfield-additional-deals-lineitem__summary">
                        <div className="swformfield-additional-deals-lineitem__summary-header">
                          Additional Deals
                        </div>
                      </div>

                      <div className="swformfield-additional-deals-lineitem__deals-list">
                        {deals
                          .filter(deal => field.value[deal.item_id])
                          .map(deal => {
                            return (
                              <DealItemReview
                                key={deal.item_id}
                                value={field.value[deal.item_id]}
                                deal={deal}
                                disabled={disabled}
                                readonly
                              />
                            );
                          })}
                      </div>
                    </div>
                  );
                }}
              />

              <AddOnTotals sale={sale} inventory={inventory} config={config} />

              <PromoCode />

              {config.validateTimedPasses && (
                <ValidatePassNumber
                  values={values}
                  actions={actions}
                  loading={loading}
                  validateForm={validateForm}
                  errors={errors}
                  validatedPasses={validatedPasses}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                />
              )}

              <FormSubmit>
                <FormBackButton
                  // disabled={disabled}
                  onClick={() => {
                    if (!hasAddOns(inventory, sale)) {
                      actions.transitionTo('/package');
                    } else {
                      actions.transitionTo('/addons');
                    }
                  }}
                >
                  Back
                </FormBackButton>
                <FormSubmitButton disabled={disabled}>
                  Check Out
                </FormSubmitButton>
              </FormSubmit>
              <FormReset>
                <FormResetButton
                  disabled={disabled}
                  onClick={() => {
                    actions.updateSale(emptySale());
                    actions.transitionTo('/');
                  }}
                >
                  Start Over
                </FormResetButton>
              </FormReset>
            </Form>
          );
        }}
      </Formik>
      <DismissableAlert>{error}</DismissableAlert>
    </FormWrapper>
  );
};

ReviewContainer.propTypes = {
  actions: PropTypes.object,
  error: PropTypes.string,
  config: PropTypes.object,
  sale: PropTypes.object,
  inventory: PropTypes.object,
  loading: PropTypes.bool,
  done: PropTypes.bool,
  validatedPasses: PropTypes.array
};

const mapStateToProps = state => ({
  ...state.sale,
  config: state.config,
  inventory: state.inventory
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(_actions, dispatch)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ReviewContainer));
